import { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, Calculator, Banknote, Check } from 'lucide-react';
import { useCartStore, useSettingsStore } from '../../hooks';

interface PaymentScreenProps {
  onPay: (paidAmount: number) => void;
  onCancel: () => void;
  loading?: boolean;
}

interface QuickAmount {
  label: string;
  value: number | 'exact';
}

const QUICK_AMOUNTS: QuickAmount[] = [
  { label: 'Uang Pas', value: 'exact' },
  { label: '10rb', value: 10000 },
  { label: '20rb', value: 20000 },
  { label: '50rb', value: 50000 },
  { label: '100rb', value: 100000 },
  { label: '200rb', value: 200000 },
];

export function PaymentScreen({ onPay, onCancel, loading }: PaymentScreenProps) {
  const { getTotal } = useCartStore();
  const { getSetting } = useSettingsStore();
  
  const taxRate = getSetting<number>('tax_rate') ?? 11;
  const taxEnabled = getSetting<boolean>('tax_enabled') !== false;
  const total = getTotal(taxRate, taxEnabled);
  
  const [paidAmount, setPaidAmount] = useState(total);
  const [inputValue, setInputValue] = useState(total.toString());

  useEffect(() => {
    setPaidAmount(total);
    setInputValue(total.toString());
  }, [total]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (loading) return;
      
      if (e.key >= '0' && e.key <= '9') {
        handleNumpad(e.key);
      } else if (e.key === 'Backspace') {
        handleBackspace();
      } else if (e.key === 'Enter') {
        if (paidAmount >= total) {
          onPay(paidAmount);
        }
      } else if (e.key === 'Escape') {
        onCancel();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [paidAmount, total, loading, onPay, onCancel]);

  const formatPrice = useCallback((price: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  }, []);

  const handleNumpad = (digit: string) => {
    const current = inputValue.replace(/\D/g, '');
    const newValue = current + digit;
    const numValue = parseInt(newValue, 10);
    setInputValue(newValue);
    setPaidAmount(numValue);
  };

  const handleBackspace = () => {
    const current = inputValue.replace(/\D/g, '');
    const newValue = current.slice(0, -1);
    const numValue = parseInt(newValue, 10) || 0;
    setInputValue(newValue);
    setPaidAmount(numValue);
  };

  const handleClear = () => {
    setInputValue('0');
    setPaidAmount(0);
  };

  const handleQuickAmount = (value: number | 'exact') => {
    if (value === 'exact') {
      setPaidAmount(total);
      setInputValue(total.toString());
    } else {
      const newAmount = paidAmount + value;
      setPaidAmount(newAmount);
      setInputValue(newAmount.toString());
    }
  };

  const change = Math.max(0, paidAmount - total);
  const canSubmit = paidAmount >= total;

  return (
    <div className="fixed inset-0 lg:inset-auto lg:top-1/2 lg:left-1/2 lg:-translate-x-1/2 lg:-translate-y-1/2 lg:max-w-4xl lg:w-full lg:h-auto lg:max-h-[90vh] lg:rounded-2xl lg:shadow-2xl bg-white lg:flex flex-col md:flex-row z-50 overflow-hidden">
      {/* Left Panel - Numpad & Quick Amounts */}
      <div className="w-full md:w-1/2 p-4 md:p-3 flex flex-col gap-3 md:h-full overflow-auto bg-gray-50 md:rounded-l-2xl">
        {/* Header - Mobile */}
        <div className="md:hidden flex items-center justify-between mb-2">
          <button
            onClick={onCancel}
            className="flex items-center gap-2 px-3 py-2 bg-white rounded-xl shadow-sm"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Kembali</span>
          </button>
        </div>

        {/* Mobile: Total & Kembalian Display - Above Numpad */}
        <div className="md:hidden bg-primary-600 text-white rounded-2xl p-4 mb-3">
          <div className="space-y-2">
            <div>
              <p className="text-primary-200 text-xs">Total Pembayaran</p>
              <p className="text-2xl font-bold">{formatPrice(total)}</p>
              {taxEnabled && (
                <p className="text-primary-300 text-xs">(termasuk pajak)</p>
              )}
            </div>
            <div className="pt-2 border-t border-primary-500">
              <p className="text-primary-200 text-xs">Kembalian</p>
              <p className={`text-xl font-bold ${change > 0 ? 'text-green-300' : 'text-primary-300'}`}>
                {formatPrice(change)}
              </p>
            </div>
          </div>
        </div>

        {/* Numpad */}
        <div className="bg-white rounded-2xl shadow-sm p-2 md:p-3">
          {/* Display */}
          <div className="bg-gray-50 rounded-xl p-3 mb-3">
            <label className="text-xs text-gray-500 block mb-1">Jumlah Bayar</label>
            <div className="text-2xl md:text-3xl font-bold text-primary-600 text-right">
              {formatPrice(paidAmount)}
            </div>
          </div>

          {/* Numpad Buttons */}
          <div className="grid grid-cols-3 gap-1 md:gap-2">
            {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((num) => (
              <button
                key={num}
                onClick={() => handleNumpad(num)}
                disabled={loading}
                className="h-12 md:h-11 bg-gray-50 hover:bg-gray-100 active:bg-gray-200 rounded-lg text-lg md:text-base font-semibold text-gray-800 transition-colors disabled:opacity-50"
              >
                {num}
              </button>
            ))}
            <button
              onClick={handleClear}
              disabled={loading}
              className="h-12 md:h-11 bg-red-50 hover:bg-red-100 active:bg-red-200 rounded-lg text-sm font-medium text-red-600 transition-colors disabled:opacity-50"
            >
              C
            </button>
            <button
              onClick={() => handleNumpad('0')}
              disabled={loading}
              className="h-12 md:h-11 bg-gray-50 hover:bg-gray-100 active:bg-gray-200 rounded-lg text-lg md:text-base font-semibold text-gray-800 transition-colors disabled:opacity-50"
            >
              0
            </button>
            <button
              onClick={handleBackspace}
              disabled={loading}
              className="h-12 md:h-11 bg-gray-50 hover:bg-gray-100 active:bg-gray-200 rounded-lg text-sm font-medium text-gray-600 transition-colors disabled:opacity-50"
            >
              ⌫
            </button>
          </div>
        </div>

        {/* Quick Amounts */}
        <div className="bg-white rounded-2xl shadow-sm p-2 md:p-3">
          <div className="flex items-center gap-2 mb-2 text-gray-600">
            <Calculator className="w-4 h-4" />
            <span className="text-sm font-medium">Quick Amount</span>
          </div>
          <div className="grid grid-cols-3 gap-1 md:gap-2">
            {QUICK_AMOUNTS.map((btn) => {
              const isExact = btn.value === 'exact';
              const isActive = isExact 
                ? paidAmount === total 
                : typeof btn.value === 'number' && paidAmount >= btn.value && (paidAmount - btn.value) % btn.value === 0;
              
              return (
                <button
                  key={btn.label}
                  onClick={() => handleQuickAmount(btn.value)}
                  disabled={loading}
                  className={`
                    py-2 px-1 md:py-2 md:px-2 rounded-lg font-medium text-sm transition-all disabled:opacity-50
                    ${isExact 
                      ? 'bg-primary-600 text-white hover:bg-primary-700 active:bg-primary-800' 
                      : isActive
                        ? 'bg-primary-100 text-primary-700 border-2 border-primary-300'
                        : 'bg-gray-50 text-gray-700 hover:bg-gray-100 active:bg-gray-200 border border-gray-200'
                    }
                  `}
                >
                  {btn.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Right Panel - Order Summary & Actions */}
      <div className="w-full md:w-1/2 p-4 md:p-3 md:h-full overflow-auto bg-white md:rounded-r-2xl">
        {/* Payment Info - Desktop: Total, Dibayar, Kembalian grouped */}
        <div className="hidden md:block space-y-3">
          {/* Total */}
          <div className="text-center">
            <p className="text-sm text-gray-500 mb-1">Total Pembayaran</p>
            <div className="text-3xl font-bold text-gray-900">
              {formatPrice(total)}
            </div>
            {taxEnabled && (
              <span className="text-xs text-gray-500">(termasuk pajak)</span>
            )}
          </div>

          {/* Dibayar & Kembalian */}
          <div className="bg-gray-50 rounded-xl p-3">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2 text-gray-600 text-sm">
                <Banknote className="w-4 h-4" />
                <span className="font-medium">Dibayar</span>
              </div>
              <span className="text-xl font-bold text-primary-600">{formatPrice(paidAmount)}</span>
            </div>
            <div className="flex justify-between items-center mt-2 pt-2 border-t border-gray-200">
              <span className="text-gray-600 font-medium text-sm">Kembalian</span>
              <span className={`text-2xl font-bold ${change > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                {formatPrice(change)}
              </span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="md:space-y-2 md:pt-0">
          {/* Mobile: Fixed Bayar Button */}
          <div className="md:hidden fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-200 shadow-lg space-y-2">
            <button
              onClick={() => onPay(paidAmount)}
              disabled={!canSubmit || loading}
              className={`
                w-full py-4 rounded-xl font-bold text-lg
                transition-all duration-200
                disabled:opacity-50 disabled:cursor-not-allowed
                flex items-center justify-center gap-2
                ${canSubmit && !loading
                  ? 'bg-primary-600 text-white hover:bg-primary-700 active:bg-primary-800 shadow-lg'
                  : 'bg-gray-200 text-gray-400'
                }
              `}
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Memproses...</span>
                </>
              ) : (
                <>
                  <Check className="w-5 h-5" />
                  <span>BAYAR {formatPrice(total)}</span>
                </>
              )}
            </button>
            <button
              onClick={onCancel}
              disabled={loading}
              className="w-full py-3 px-4 rounded-xl font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Batal
            </button>
          </div>

          {/* Desktop: Action Buttons */}
          <div className="hidden md:flex flex-col gap-2">
            <button
              onClick={() => onPay(paidAmount)}
              disabled={!canSubmit || loading}
              className={`
                w-full py-3 rounded-xl font-semibold text-base
                transition-all duration-200
                disabled:opacity-50 disabled:cursor-not-allowed
                flex items-center justify-center gap-2
                ${canSubmit && !loading
                  ? 'bg-primary-600 text-white hover:bg-primary-700 active:bg-primary-800'
                  : 'bg-gray-200 text-gray-400'
                }
              `}
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Memproses...</span>
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  <span>BAYAR {formatPrice(total)}</span>
                </>
              )}
            </button>
            <button
              onClick={onCancel}
              disabled={loading}
              className="w-full py-2 px-4 rounded-xl font-medium border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50 text-sm"
            >
              Batal
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
